// Glocke und Benachrichtigungszentrale (design-system §3.7): „Neu“ und „Alle“ für Dinge, die man wissen sollte,
// dazu „Aufgaben“ für Dinge, die zu erledigen sind. Der rote Punkt an der Glocke erscheint nur bei Ungelesenem
// mit Dringlichkeit – alles andere zählt still.
import { Bell, Check, CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import type { z } from 'zod'
import { formatDate } from '@shared/format'
import { businessDate } from '@shared/time'
import {
  notificationList,
  notificationMarkAllRead,
  notificationMarkRead,
  taskList,
  taskResolve,
  type zNotification,
  type zTask,
} from '@modules/platform/contract'
import { useEntityLinks } from '../api/context'
import { skipToken, useActionMutation, useActionQuery } from '../api/hooks'
import { canRunAction, hasPermission } from '../api/permissions'
import { useSession } from '../api/session'
import { cn } from '../lib/cn'
import { shell, useShellStore, type NotificationTab } from '../stores/shell'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Drawer } from '../ui/Drawer'
import { EmptyState, ErrorState } from '../ui/EmptyState'
import { DateText } from '../ui/format'
import { IconButton } from '../ui/IconButton'
import { Skeleton } from '../ui/Skeleton'
import { Tabs } from '../ui/Tabs'

type Notification = z.output<typeof zNotification>
type Task = z.output<typeof zTask>

const SEVERITY: Record<Notification['severity'], { icon: LucideIcon; tone: string }> = {
  info: { icon: Info, tone: 'text-info-fg' },
  success: { icon: CircleCheck, tone: 'text-success-fg' },
  warning: { icon: TriangleAlert, tone: 'text-warning-fg' },
  danger: { icon: CircleAlert, tone: 'text-danger-fg' },
}

const isUrgent = (notification: Notification): boolean =>
  notification.severity === 'warning' || notification.severity === 'danger'

/** Ungelesenes für die Glocke; lädt bei jedem Echtzeit-Hinweis und sonst einmal je Minute nach. */
function useUnread() {
  const session = useSession()
  const allowed = canRunAction(session, notificationList, 'any')
  return useActionQuery(notificationList, allowed ? { unreadOnly: true, page: { limit: 30 } } : skipToken, {
    refreshOn: '*',
    refetchInterval: 60_000,
    staleTime: 20_000,
  })
}

/** Offene Aufgaben (für den Zähler am Reiter und die Startseite). */
export function useOpenTasks(options: { limit?: number } = {}) {
  const session = useSession()
  const allowed = canRunAction(session, taskList, 'any')
  return useActionQuery(taskList, allowed ? { status: 'open', page: { limit: options.limit ?? 30 } } : skipToken, {
    refreshOn: '*',
    refetchInterval: 120_000,
    staleTime: 20_000,
  })
}

export function NotificationBell({ className }: { className?: string }) {
  const unread = useUnread()
  const count = unread.data?.unreadCount ?? 0
  const urgent = unread.data?.rows.some(isUrgent) ?? false
  return (
    <span className={cn('relative inline-flex', className)}>
      <IconButton
        icon={Bell}
        label={count > 0 ? `Benachrichtigungen, ${count} neu` : 'Benachrichtigungen'}
        badge={urgent ? 'dot' : undefined}
        onClick={() => shell.openNotifications(count > 0 ? 'new' : 'all')}
        data-testid="notification-bell"
      />
      {count > 0 && !urgent && (
        <span
          className="pointer-events-none absolute top-1.5 right-1.5 size-1.5 rounded-full bg-fg-subtle touch:top-3 touch:right-3"
          aria-hidden
        />
      )}
    </span>
  )
}

function NotificationRow({ notification, onOpen }: { notification: Notification; onOpen: (n: Notification) => void }) {
  const { icon: Icon, tone } = SEVERITY[notification.severity]
  const unread = notification.readAt === null
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(notification)}
        className="flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-left transition-colors duration-(--duration-instant) hover:bg-hover focus-inset"
      >
        <span className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-sunken', tone)}>
          <Icon className="size-3.5" strokeWidth={1.75} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn('block text-dense text-fg touch:text-touch', unread && 'font-medium')}>
            {notification.title}
          </span>
          {notification.body && <span className="mt-0.5 block text-dense text-fg-muted">{notification.body}</span>}
          <span className="mt-1 flex items-center gap-2 text-caption text-fg-subtle">
            <DateText value={notification.createdAt} format="relative" />
            {notification.generatedByAi && <Badge size="sm">über KI-Assistent</Badge>}
          </span>
        </span>
        {unread && <span className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" aria-label="ungelesen" />}
      </button>
    </li>
  )
}

function groupByDay<T>(rows: readonly T[], at: (row: T) => string): { day: string; rows: T[] }[] {
  const groups: { day: string; rows: T[] }[] = []
  for (const row of rows) {
    const day = businessDate(new Date(at(row)))
    const last = groups[groups.length - 1]
    if (last && last.day === day) last.rows.push(row)
    else groups.push({ day, rows: [row] })
  }
  return groups
}

function NotificationList({ unreadOnly, onOpen }: { unreadOnly: boolean; onOpen: (n: Notification) => void }) {
  const query = useActionQuery(notificationList, { unreadOnly, page: { limit: 50 } }, { refreshOn: '*' })
  const today = businessDate()
  const groups = useMemo(() => groupByDay(query.data?.rows ?? [], (row) => row.createdAt), [query.data])
  if (query.isPending) return <Skeleton variant="text" lines={6} />
  if (query.error)
    return (
      <ErrorState
        size="sm"
        title="Benachrichtigungen konnten nicht geladen werden"
        message={query.error.message}
        onRetry={() => void query.refetch()}
      />
    )
  if (groups.length === 0) {
    return (
      <EmptyState
        variant="first-use"
        size="sm"
        icon={Bell}
        title={unreadOnly ? 'Nichts Neues' : 'Noch keine Benachrichtigungen'}
        description={
          unreadOnly
            ? 'Sie haben alles gelesen.'
            : 'Hier erscheint, was Sie wissen sollten: bestätigte Bestellungen, Preisänderungen, fertige Exporte.'
        }
      />
    )
  }
  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <section key={group.day}>
          <h3 className="mb-1 px-2 text-micro text-fg-subtle uppercase">
            {group.day === today ? 'Heute' : formatDate(group.day, { weekday: true })}
          </h3>
          <ul className="flex flex-col">
            {group.rows.map((notification) => (
              <NotificationRow key={notification.id} notification={notification} onOpen={onOpen} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function TaskRow({ task, onOpen }: { task: Task; onOpen: (href: string) => void }) {
  const session = useSession()
  const entityHref = useEntityLinks()
  const resolve = useActionMutation(taskResolve, { successMessage: 'Aufgabe erledigt' })
  const location = session?.locations.find((entry) => entry.id === task.locationId)
  const href = task.entity ? entityHref(task.entity.type, task.entity.id) : null
  const mayResolve = hasPermission(session, taskResolve.permission, task.locationId ?? 'any')
  const overdue = task.dueDate !== null && task.dueDate < businessDate()
  return (
    <li className="flex flex-col gap-2 rounded-md border border-line bg-surface px-3 py-2.5">
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1">
          <span className="block text-dense font-medium text-fg touch:text-touch">{task.title}</span>
          {task.body && <span className="mt-0.5 block text-dense text-fg-muted">{task.body}</span>}
        </span>
        {location && (
          <Badge size="sm" className="shrink-0">
            {location.code}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className={cn('flex-1 text-caption', overdue ? 'text-danger-fg' : 'text-fg-subtle')}>
          {task.dueDate ? (
            <>
              {overdue ? 'überfällig seit ' : 'fällig '}
              <DateText value={task.dueDate} short />
            </>
          ) : (
            <>
              seit <DateText value={task.createdAt} short />
            </>
          )}
        </span>
        {href && (
          <Button size="sm" variant="ghost" onClick={() => onOpen(href)}>
            Öffnen
          </Button>
        )}
        {mayResolve && (
          <Button size="sm" icon={Check} loading={resolve.isPending} onClick={() => resolve.mutate({ id: task.id })}>
            Erledigt
          </Button>
        )}
      </div>
    </li>
  )
}

function TaskList({ onOpen }: { onOpen: (href: string) => void }) {
  const session = useSession()
  const tasks = useOpenTasks({ limit: 50 })
  if (!canRunAction(session, taskList, 'any')) {
    return <EmptyState variant="no-permission" size="sm" title="Für „Aufgaben sehen“ fehlt Ihnen das Recht." />
  }
  if (tasks.isPending) return <Skeleton variant="text" lines={6} />
  if (tasks.error)
    return (
      <ErrorState
        size="sm"
        title="Aufgaben konnten nicht geladen werden"
        message={tasks.error.message}
        onRetry={() => void tasks.refetch()}
      />
    )
  if ((tasks.data?.rows.length ?? 0) === 0) {
    return (
      <EmptyState
        variant="first-use"
        size="sm"
        icon={Check}
        title="Nichts zu erledigen"
        description="Aufgaben entstehen aus den Vorgängen: erwartete Wareneingänge, Umlagerungen im Transit, Zählaufträge."
      />
    )
  }
  return (
    <ul className="flex flex-col gap-2">
      {tasks.data?.rows.map((task) => (
        <TaskRow key={task.id} task={task} onOpen={onOpen} />
      ))}
    </ul>
  )
}

/** Drawer an der Glocke. Steht einmal im App-Rahmen. */
export function NotificationCenter() {
  const open = useShellStore((state) => state.notificationsOpen)
  const tab = useShellStore((state) => state.notificationsTab)
  const session = useSession()
  const navigate = useNavigate()
  const entityHref = useEntityLinks()
  const unread = useUnread()
  const tasks = useOpenTasks()
  const markRead = useActionMutation(notificationMarkRead, { invalidate: [notificationList], errorToast: false })
  const markAll = useActionMutation(notificationMarkAllRead, { invalidate: [notificationList] })
  const unreadCount = unread.data?.unreadCount ?? 0
  const canNotifications = canRunAction(session, notificationList, 'any')

  const go = (href: string): void => {
    shell.setNotifications(false)
    void navigate(href)
  }
  const openNotification = (notification: Notification): void => {
    if (notification.readAt === null) markRead.mutate({ ids: [notification.id] })
    const href =
      notification.link ?? (notification.entity ? entityHref(notification.entity.type, notification.entity.id) : null)
    if (href) go(href)
  }

  return (
    <Drawer
      open={open}
      onOpenChange={shell.setNotifications}
      title="Benachrichtigungen"
      size="sm"
      data-testid="notification-center"
      footer={
        tab !== 'tasks' && unreadCount > 0 ? (
          <Button variant="ghost" loading={markAll.isPending} onClick={() => markAll.mutate({})}>
            Alle als gelesen markieren
          </Button>
        ) : undefined
      }
    >
      <div className="-mx-2 flex flex-col gap-4">
        <Tabs<NotificationTab>
          value={tab}
          onChange={shell.setNotificationsTab}
          variant="pill"
          overflow="scroll"
          ariaLabel="Benachrichtigungen und Aufgaben"
          className="px-2"
          items={[
            { value: 'new', label: 'Neu', count: unreadCount || undefined },
            { value: 'all', label: 'Alle' },
            { value: 'tasks', label: 'Aufgaben', count: tasks.data?.rows.length || undefined },
          ]}
        />
        {tab === 'tasks' ? (
          <div className="px-2">
            <TaskList onOpen={go} />
          </div>
        ) : canNotifications ? (
          <NotificationList key={tab} unreadOnly={tab === 'new'} onOpen={openNotification} />
        ) : (
          <div className="px-2">
            <EmptyState
              variant="no-permission"
              size="sm"
              title="Benachrichtigungen sind für Ihren Zugang nicht verfügbar."
            />
          </div>
        )}
      </div>
    </Drawer>
  )
}
